// Radha — indirect tax: outward-supply summary + e-invoicing clearance status,
// computed live from the invoice reads. No new tables.
//
// Regime-driven since 072. The identifier labels, the regime's name and whether
// a clearance step exists at all come from einvoice_regimes for the tenant's
// country; only the intra/inter-state split below is India-specific and is
// rendered only for an India tenant.
//
// Intra-state (CGST+SGST) vs inter-state (IGST) is derived by comparing the
// customer's GSTIN state code (first 2 digits) against the tenant's own GSTIN
// (surfaced via auth_me, migration 041) — the standard GST rule. The schema
// carries tax_amount as a single total (no CGST/SGST/IGST split stored), so this
// shows the correct intra/inter split of invoice counts and value, not a
// fabricated component breakdown.
import React, { useMemo } from "react";

import { useInvoices } from "../hooks/useBatchModules";
import type { Invoice } from "../hooks/useBatchModules";
import { useEinvoiceRegime } from "../hooks/useEinvoice";
import { useMe } from "../lib/authSupabase";
import { useViewCountry } from "../lib/jurisdiction";
import { ExportButton } from "../components/TableSearch";
import { fmtMoney as inr, toBase } from "../lib/money";

// The regime table stores network keys; these are how they are written.
const NETWORK_LABEL: Record<string, string> = {
  nic_irp: "NIC IRP", zatca_fatoora: "ZATCA Fatoora", peppol: "Peppol",
  sdi: "SdI", ksef: "KSeF",
};


const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "9px 8px" };
const numTd: React.CSSProperties = { ...td, textAlign: "right", whiteSpace: "nowrap" };

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div title={hint}>
      <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>{label}</div>
      <div style={{ font: "600 20px Inter" }}>{value}</div>
    </div>
  );
}

const stateCode = (gstin: string | null) => (gstin && gstin.length >= 2 ? gstin.slice(0, 2) : null);

export function GstPanel() {
  const { data, isLoading, error } = useInvoices();
  const me = useMe(true);
  const viewCountry = useViewCountry(me?.tenant_country);
  const { data: regime } = useEinvoiceRegime(viewCountry);
  const isIndia = viewCountry === "IN";
  const tenantState = stateCode(me?.tenant_gstin ?? null);

  const m = useMemo(() => {
    const list = (data ?? []) as Invoice[];
    // Returns are filed in the tenant's own currency, so every value here is
    // converted at the rate booked at issue before it is added to anything —
    // an AED invoice summed as if it were rupees inflates the return.
    const taxable = (i: Invoice) => toBase(i.taxable_amount, i.fx_rate_at_issue);
    const tax = (i: Invoice) => toBase(i.tax_amount, i.fx_rate_at_issue);
    const gross = (i: Invoice) => toBase(i.total_amount, i.fx_rate_at_issue);
    const foreign = list.filter((i) => i.currency !== (me?.tenant_base_currency ?? "INR"));

    const b2b = list.filter((i) => !!i.customer_gstin);
    const b2c = list.filter((i) => !i.customer_gstin);
    const intra = tenantState ? b2b.filter((i) => stateCode(i.customer_gstin) === tenantState) : [];
    const inter = tenantState ? b2b.filter((i) => stateCode(i.customer_gstin) !== tenantState) : [];

    // Clearance state, country-neutral. Rows predating 072 have no
    // clearance_status, so fall back to the India columns they do have.
    const cstate = (i: Invoice) =>
      i.clearance_status ?? (i.irn ? "cleared" : i.irn_required ? "pending" : "not_required");
    const irnRequired = list.filter((i) => cstate(i) !== "not_required");
    const irnGenerated = irnRequired.filter((i) => cstate(i) === "cleared");
    const irnPending = irnRequired.filter((i) => cstate(i) === "pending");
    const irnFailed = irnRequired.filter((i) => cstate(i) === "rejected" || cstate(i) === "failed");

    const byGstin = new Map<string, { customer: string; count: number; taxable: number; tax: number; total: number }>();
    for (const i of b2b) {
      const key = i.customer_gstin as string;
      const e = byGstin.get(key) ?? { customer: i.customer_name, count: 0, taxable: 0, tax: 0, total: 0 };
      e.count += 1; e.taxable += taxable(i); e.tax += tax(i); e.total += gross(i);
      byGstin.set(key, e);
    }
    const gstinRows = [...byGstin.entries()]
      .map(([gstin, v]) => ({ gstin, ...v }))
      .sort((a, b) => b.taxable - a.taxable);

    return {
      taxable: list.reduce((s, i) => s + taxable(i), 0),
      tax: list.reduce((s, i) => s + tax(i), 0),
      total: list.reduce((s, i) => s + gross(i), 0),
      b2bCount: b2b.length, b2cCount: b2c.length,
      foreignCount: foreign.length,
      intraCount: intra.length, intraValue: intra.reduce((s, i) => s + taxable(i), 0),
      interCount: inter.length, interValue: inter.reduce((s, i) => s + taxable(i), 0),
      irnRequired: irnRequired.length, irnGenerated: irnGenerated.length,
      irnPending: irnPending.length, irnFailed: irnFailed.length,
      gstinRows,
    };
  }, [data, tenantState, me?.tenant_base_currency]);

  if (isLoading) return <p style={{ padding: 24 }}>Loading…</p>;
  if (error) return <p style={{ padding: 24, color: "var(--status-critical)" }}>{(error as Error).message}</p>;

  // Every user-facing name for an identifier comes from the pack, so a UAE or
  // Italian tenant is never shown Indian vocabulary.
  const taxIdLabel = regime?.tax_id_label ?? "GSTIN";
  const clearanceIdLabel = regime?.clearance_id_label ?? "IRN";
  const model = regime?.model ?? "clearance";

  return (
    <div>
      {me?.tenant_gstin && (
        <p style={{ color: "var(--fg-muted)", fontSize: 13, margin: "0 0 16px" }}>
          {me.tenant_legal_name} · {taxIdLabel} {me.tenant_gstin}
          {isIndia && tenantState && ` (state ${tenantState})`}
        </p>
      )}

      <div style={{ display: "flex", gap: 26, margin: "4px 0 22px", flexWrap: "wrap" }}>
        <Stat label="Taxable value" value={inr(m.taxable)} />
        <Stat label={isIndia ? "GST (tax amount)" : "Tax amount"} value={inr(m.tax)} />
        <Stat label="Invoice value" value={inr(m.total)} />
        <Stat label="B2B / B2C" value={`${m.b2bCount} / ${m.b2cCount}`} hint={`Invoices with a customer ${taxIdLabel} vs without.`} />
        {m.foreignCount > 0 && (
          <Stat label="Foreign-currency" value={String(m.foreignCount)}
            hint={`Invoices not denominated in ${me?.tenant_base_currency ?? "the base currency"}. Values above are converted at the rate booked at issue. Export supplies are reported separately from domestic B2B; Radha does not classify them yet, so they are included in the table below.`} />
        )}
        {isIndia && (
          <>
            <Stat label="Intra-state (CGST+SGST)" value={String(m.intraCount)} hint={`Taxable value ${inr(m.intraValue)} — customer in the same state as ${me?.tenant_legal_name ?? "the tenant"}.`} />
            <Stat label="Inter-state (IGST)" value={String(m.interCount)} hint={`Taxable value ${inr(m.interValue)}.`} />
          </>
        )}
      </div>

      <div style={{ font: "600 15px Inter", margin: "0 0 4px" }}>
        {regime ? regime.regime_name : "E-invoicing"}
        {model !== "none" && clearanceIdLabel && (
          <span style={{ color: "var(--fg-muted)", fontWeight: 400 }}> · {clearanceIdLabel} status</span>
        )}
      </div>
      <p style={{ color: "var(--fg-muted)", fontSize: 12, margin: "0 0 10px", maxWidth: 720 }}>
        {model === "clearance" && `Clearance model — an invoice is not valid until the portal returns ${clearanceIdLabel ? `the ${clearanceIdLabel}` : "an identifier"}${regime?.requires_qr ? " and a signed QR code" : ""}.`}
        {model === "post_audit" && "Post-audit model — invoices are issued freely and transmitted or reported afterwards, with no pre-clearance step."}
        {model === "none" && "No B2B e-invoicing mandate in this jurisdiction; the counts below reflect invoices flagged for clearance anyway."}
        {regime?.statute_ref && <span style={{ color: "var(--fg-faint)" }}> {regime.statute_ref}.</span>}
        {regime?.phase_note && <span style={{ color: "var(--fg-faint)" }}> {regime.phase_note}</span>}
      </p>
      <div style={{ display: "flex", gap: 26, margin: "0 0 22px", flexWrap: "wrap" }}>
        <Stat label={`${clearanceIdLabel ?? "Clearance"} required`} value={String(m.irnRequired)} />
        <Stat label={model === "post_audit" ? "Transmitted" : "Cleared"} value={String(m.irnGenerated)} />
        <Stat label="Pending" value={String(m.irnPending)} hint={m.irnPending > 0 ? `Invoices needing clearance that have no ${clearanceIdLabel ?? "identifier"} yet.` : undefined} />
        {m.irnFailed > 0 && <Stat label="Rejected / failed" value={String(m.irnFailed)} hint="Returned by the portal with an error — these need correcting and resubmitting." />}
      </div>
      {regime?.network && (
        <p style={{ color: "var(--fg-faint)", fontSize: 11.5, margin: "-14px 0 22px" }}>
          Network: {NETWORK_LABEL[regime.network] ?? regime.network} — Radha tracks status and prepares the payload; transmitting to
          the portal needs production credentials (an accredited provider or access point), which are a commercial step,
          not a code one.
        </p>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", margin: "0 0 6px" }}>
        <div style={{ font: "600 15px Inter" }}>Outward supplies by recipient {taxIdLabel} (B2B)</div>
        <ExportButton name={isIndia ? "GSTR-1 B2B" : "B2B outward supplies"} rows={() => m.gstinRows.map((r) => ({
          [taxIdLabel]: r.gstin, Customer: r.customer, Invoices: r.count,
          "Taxable value": r.taxable, "Tax amount": r.tax, "Invoice value": r.total,
        }))} />
      </div>
      <div className="sanctuary-scroll-x">
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12 }}>
            <th style={th}>{taxIdLabel}</th><th style={th}>Customer</th><th style={{ ...th, textAlign: "right" }}>Invoices</th>
            <th style={{ ...th, textAlign: "right" }}>Taxable value</th><th style={{ ...th, textAlign: "right" }}>Tax</th><th style={{ ...th, textAlign: "right" }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {m.gstinRows.map((r) => (
            <tr key={r.gstin} style={{ borderTop: "1px solid var(--border)" }}>
              <td style={td}>{r.gstin}</td>
              <td style={td}>{r.customer}</td>
              <td style={numTd}>{r.count}</td>
              <td style={numTd}>{inr(r.taxable)}</td>
              <td style={numTd}>{inr(r.tax)}</td>
              <td style={numTd}>{inr(r.total)}</td>
            </tr>
          ))}
          {m.gstinRows.length === 0 && (
            <tr><td style={{ ...td, color: "var(--fg-muted)" }} colSpan={6}>No B2B invoices yet.</td></tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
}
